/**
 * Setting Content Systems Page
 */

// React
import React, { FC } from 'react';
import Grid from '@mui/material/Grid2';
import { Controller } from 'react-hook-form';

// Libraries
import { Box, CardContent, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';

// Components
import HeaderNavigate from 'components/HeaderNavigate';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import AppInput from 'components/AppInput';
import PreviewImageModal from 'components/PreviewImageModal';
import ImageUpload from 'components/ImageUpload';
import AppRadio from 'components/AppRadio';
import AppInputCustom from 'components/AppInputCustom';

// Common styles
import {
  ButtonUpdate,
  PageNavigateContainer,
  RadioGroupContainer,
  WrapperContentSystem,
  WrapperScroll,
} from 'styles';

// Hooks
import { useSettingContentSystem } from './useSettingContentSystem';

// Helper
import { hostName } from 'utils/helper/common';

// Router
import ROUTES from 'configs/route';

export const TypographyCustom = styled(Typography)(({ theme }) => ({
  marginBottom: '20px',
  fontWeight: '500',
  fontSize: '16px',
  color: '#666666',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    marginBottom: '0px',
  },
}));

const NotifyContainer = styled(Box)<{ severity: string }>(({ severity, theme }) => ({
  width: '100%',
  height: '47px',
  color: '#ffffff',
  padding: '16px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: '10px',
  backgroundColor: severity === 'success' ? theme.palette.success.light : theme.palette.error.light,
  marginBottom: '33px',
}));

const CardContentCustom = styled(CardContent)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
  },
}));

// Component
const SettingContentSystemPage: FC = () => {
  const { service_id } = useParams();

  const {
    control,
    handleSubmit,
    handleUpdate,
    handleClose,
    handleConfirmDelete,
    handleButtonDeleteImage,
    handleButtonPreviewImage,
    handleFileChange,
    handleConfirmUpdate,
    formData,
    modalConfirm,
    modalPreview,
    modalConfirmUpdate,
    errors,
    imagePreview,
    messageErrorIcon,
    messageErrorIconActive,
    messageErrorIconDeactive,
    isShowValidateImage,
    notifyCustom,
  } = useSettingContentSystem(Number(service_id));

  return (
    <form onSubmit={handleSubmit(handleUpdate)}>
      <HeaderNavigate
        linkHref={ROUTES.SYSTEM_APPLICATION_SETTING}
        linkText="システム設定"
        currentText="登録情報編集"
      />
      <ConfirmDeleteModal
        open={modalConfirm}
        url={hostName}
        messages="ファイルを削除して宜しいですか？"
        handleClose={handleClose}
        handleConfirmDelete={handleConfirmDelete}
      />
      <ConfirmDeleteModal
        open={modalConfirmUpdate}
        url={hostName}
        messages="内容を保存して宜しいですか？"
        handleClose={handleClose}
        handleConfirmDelete={handleConfirmUpdate}
      />
      <PreviewImageModal open={modalPreview} image={imagePreview} handleClose={handleClose} />
      <PageNavigateContainer>
        <WrapperScroll>
          {notifyCustom.open && (
            <NotifyContainer severity={notifyCustom.severity}>
              <Typography>{notifyCustom.title}</Typography>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon sx={{ width: '13px', height: 'auto', color: '#AAAAAA' }} />
              </IconButton>
            </NotifyContainer>
          )}

          <WrapperContentSystem className="table-container">
            <CardContentCustom>
              <Grid container size={12} spacing={1} marginBottom={{ xs: 3, md: 5 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TypographyCustom variant="h4">利用 ON/OFF</TypographyCustom>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Controller
                    name="is_displayed"
                    control={control}
                    defaultValue={formData.is_displayed}
                    render={({ field: radioField }) => (
                      <RadioGroupContainer {...radioField}>
                        <AppRadio value={1} label={'ON'} />
                        <AppRadio value={0} label={'OFF'} />
                      </RadioGroupContainer>
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container size={12} spacing={1} marginBottom={{ xs: 3, md: 5 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TypographyCustom variant="h4">表示順</TypographyCustom>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <AppInputCustom
                    sx={{ width: '163px' }}
                    name="position"
                    placeholder="表示順"
                    control={control}
                    type="text"
                    errors={errors}
                    helperText={errors?.['position']?.message}
                  />
                </Grid>
              </Grid>
              <Grid container size={12} spacing={1} marginBottom={{ xs: 3, md: 5 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TypographyCustom variant="h4">システム名</TypographyCustom>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <AppInputCustom
                    name="title"
                    placeholder="システム名"
                    control={control}
                    type="text"
                    errors={errors}
                    helperText={errors?.['title']?.message}
                  />
                </Grid>
              </Grid>
              <Grid container size={12} spacing={1} marginBottom={{ xs: 3, md: 5 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TypographyCustom variant="h4">システム説明文</TypographyCustom>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <AppInput
                    isTextarea={true}
                    name="body"
                    placeholder="システム説明文"
                    control={control}
                    type="text"
                    errors={errors}
                    helperText={errors?.['body']?.message}
                  />
                </Grid>
              </Grid>
              <Grid container size={12} spacing={1} marginBottom={{ xs: 3, md: 5 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TypographyCustom variant="h4">システムURL</TypographyCustom>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <AppInput
                    isTextarea={true}
                    rows={2}
                    name="callback_url"
                    placeholder="システムURL"
                    control={control}
                    type="text"
                    errors={errors}
                    helperText={errors?.['callback_url']?.message}
                  />
                </Grid>
              </Grid>
              <ImageUpload
                title="システムアイコン（ホーム画面）"
                handleFileChange={handleFileChange}
                handleButtonDeleteImage={handleButtonDeleteImage}
                handleButtonPreviewImage={handleButtonPreviewImage}
                icon={formData?.icon || ''}
                iconName="icon"
                file={formData?.icon_upload}
                size={formData?.icon_size}
                error={messageErrorIcon}
                isShowMessage={isShowValidateImage}
              />
              <ImageUpload
                title="システムアイコン（AF管理画面（利用中））"
                handleFileChange={handleFileChange}
                handleButtonDeleteImage={handleButtonDeleteImage}
                handleButtonPreviewImage={handleButtonPreviewImage}
                icon={formData?.icon_status_active || ''}
                iconName="icon_status_active"
                file={formData?.icon_status_active_upload}
                size={formData?.icon_status_active_size}
                error={messageErrorIconActive}
                isShowMessage={isShowValidateImage}
              />
              <ImageUpload
                title="システムアイコン（AF管理画面（申請中））"
                handleFileChange={handleFileChange}
                handleButtonDeleteImage={handleButtonDeleteImage}
                handleButtonPreviewImage={handleButtonPreviewImage}
                icon={formData?.icon_status_deactive || ''}
                iconName="icon_status_deactive"
                file={formData?.icon_status_deactive_upload}
                size={formData?.icon_status_deactive_size}
                error={messageErrorIconDeactive}
                isShowMessage={isShowValidateImage}
              />
              <Grid container size={12} spacing={1} marginBottom={{ xs: 3, md: 5 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TypographyCustom variant="h4">カラーコード</TypographyCustom>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <AppInputCustom
                    name="color"
                    placeholder="カラーコード"
                    control={control}
                    type="text"
                    errors={errors}
                    helperText={errors?.['color']?.message}
                  />
                </Grid>
              </Grid>
            </CardContentCustom>
          </WrapperContentSystem>
        </WrapperScroll>
        <ButtonUpdate type="submit" variant="contained">
          保存する
        </ButtonUpdate>
      </PageNavigateContainer>
    </form>
  );
};

export default SettingContentSystemPage;
